import { getAuditActor } from '@/services/audit/auditContext';
import { appendAuditLog } from '@/services/audit/auditRepository';
import { getChangedFields } from '@/services/audit/getChangedFields';
import { sanitizeSnapshot } from '@/services/audit/sanitizeSnapshot';
import type {
  AuditEntityType,
  AuditLogInput,
} from '@/types/audit';

type EntityAuditParams = {
  entityType: AuditEntityType;
  entityId: string;
  before?: Record<string, unknown> | undefined;
  after?: Record<string, unknown> | undefined;
  changedFields?: string[] | undefined;
  metadata?: Record<string, unknown> | undefined;
};

async function writeAudit(input: AuditLogInput): Promise<void> {
  const actor = getAuditActor();
  if (!actor) return;

  try {
    await appendAuditLog(input, actor);
  } catch {
    // La operación de negocio ya persistió; no fallar el flujo principal.
  }
}

function buildChanged(
  before?: Record<string, unknown>,
  after?: Record<string, unknown>,
  provided?: string[],
): string[] | undefined {
  if (provided) return provided;
  if (before && after) return getChangedFields(before, after);
  return undefined;
}

function toAuditInput(
  action: AuditLogInput['action'],
  params: EntityAuditParams,
): AuditLogInput {
  const before = sanitizeSnapshot(params.before);
  const after = sanitizeSnapshot(params.after);
  return {
    action,
    entityType: params.entityType,
    entityId: params.entityId,
    ...(before !== undefined ? { before } : {}),
    ...(after !== undefined ? { after } : {}),
    ...(params.changedFields !== undefined
      ? { changedFields: params.changedFields }
      : {}),
    ...(params.metadata !== undefined ? { metadata: params.metadata } : {}),
  };
}

export async function logCreate(params: EntityAuditParams): Promise<void> {
  await writeAudit(toAuditInput('create', params));
}

export async function logUpdate(params: EntityAuditParams): Promise<void> {
  const input = toAuditInput('update', params);
  const changed = buildChanged(input.before, input.after, params.changedFields);
  await writeAudit({
    ...input,
    ...(changed !== undefined ? { changedFields: changed } : {}),
  });
}

export async function logStatusChange(params: EntityAuditParams): Promise<void> {
  const input = toAuditInput('status_change', params);
  const changed = buildChanged(input.before, input.after, params.changedFields);
  await writeAudit({
    ...input,
    ...(changed !== undefined ? { changedFields: changed } : {}),
  });
}

export async function logRoleChange(params: EntityAuditParams): Promise<void> {
  const input = toAuditInput('role_change', params);
  const changed = buildChanged(input.before, input.after, params.changedFields);
  await writeAudit({
    ...input,
    ...(changed !== undefined ? { changedFields: changed } : {}),
  });
}

export async function logDelete(params: EntityAuditParams): Promise<void> {
  await writeAudit(toAuditInput('delete', params));
}

export async function logRestore(params: EntityAuditParams): Promise<void> {
  await writeAudit(toAuditInput('restore', params));
}

export async function logPermissionDenied(params: {
  entityType?: AuditEntityType;
  entityId?: string;
  metadata?: Record<string, unknown> | undefined;
}): Promise<void> {
  await writeAudit({
    action: 'permission_denied',
    entityType: params.entityType ?? 'system',
    entityId: params.entityId ?? 'access',
    ...(params.metadata !== undefined ? { metadata: params.metadata } : {}),
  });
}

export async function logLogin(): Promise<void> {
  const actor = getAuditActor();
  if (!actor) return;
  await writeAudit({
    action: 'login',
    entityType: 'system',
    entityId: actor.uid,
    metadata: { source: 'web' },
  });
}

export async function logLogout(): Promise<void> {
  const actor = getAuditActor();
  if (!actor) return;
  await writeAudit({
    action: 'logout',
    entityType: 'system',
    entityId: actor.uid,
    metadata: { source: 'web' },
  });
}

export { listAuditLogs } from '@/services/audit/auditRepository';
